import { useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { Box, Button } from '@factorialco/deprecated-design-system'
import { LOCAL_STORAGE_KEYS } from 'config/Vars'

import { observer } from 'mobx-react'

import Callout from 'modules/core/components/Callout'
import Modal from 'modules/core/components/Modal'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'
import useLocalStorage from 'modules/core/lib/useLocalStorage'

import { useSchedule } from 'modules/shift_management/hooks/useSchedule'
import { trackOpenTimeInsightsSidepanelSubmit } from 'modules/shift_management/lib/trackEvents'

import TeamsLocationsSelector from './TeamsLocationsSelector'

interface Props {
  onClose: () => void
  canSeeInsightsForEverybody: boolean | undefined
}

const InsightsRequestModal = ({ onClose, canSeeInsightsForEverybody }: Props) => {
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const [params] = useSearchParams()
  const [{ view }] = useSchedule()
  const [showQuickTip, setShowQuickTip] = useState<boolean>(true)
  const [stopShowingQuickTip, setStopShowingQuickTip] = useLocalStorage<boolean>(
    LOCAL_STORAGE_KEYS.shiftManagementHideInsightsQuickTip,
    false
  )

  const [teamsIdsSelection, setTeamsIdsSelection] = useLocalStorage<number[]>(
    LOCAL_STORAGE_KEYS.shiftManagementInsightsTeamSelection,
    []
  )

  const [locationIdsSelection, setLocationsIdsSelection] = useLocalStorage<number[]>(
    LOCAL_STORAGE_KEYS.shiftManagementInsightsLocationSelection,
    []
  )

  const [teamsIds, setTeamsIds] = useState<number[]>(teamsIdsSelection ?? [])
  const [locationsIds, setLocationsIds] = useState<number[]>(locationIdsSelection ?? [])

  const hasSelectedTeamOrLocation = !!(teamsIds.length || locationsIds.length)

  const calloutTitle = i18n.t('shift_management.time_insights.request_modal.callout.title')
  const calloutDescription = i18n.t(
    'shift_management.time_insights.request_modal.callout.description'
  )

  const showCallout = showQuickTip && !stopShowingQuickTip

  const onSubmit = () => {
    trackOpenTimeInsightsSidepanelSubmit(teamsIds, locationsIds)

    teamsIds.forEach((teamId) => params.append('teamsIds', teamId.toString()))
    locationsIds.forEach((locationId) => params.append('locationsIds', locationId.toString()))

    navigate({
      pathname: navigation.shifts.customViewGroup.insightsSidepanel.fullPath({
        viewType: view.viewType,
        groupType: view.groupType,
        year: view.startRangeYear,
        month: view.startRangeMonth,
        day: view.startRangeDay,
      }),
      search: params.toString(),
    })
  }

  return (
    <Modal
      illustration="document-edit"
      title={i18n.t('shift_management.time_insights.title')}
      description={i18n.t('shift_management.export_annual_balance.modal.description_2')}
      onSubmit={onSubmit}
      onClose={onClose}
      footer={
        <>
          <Button type="secondary" onClick={onClose}>
            {i18n.t('common.cancel')}
          </Button>
          <Button disabled={!hasSelectedTeamOrLocation} submit>
            {i18n.t('common.submit')}
          </Button>
        </>
      }
    >
      <Box gap="s24">
        <TeamsLocationsSelector
          locationsIds={locationIdsSelection}
          setLocationsIds={setLocationsIds}
          setLocationsIdsSelection={setLocationsIdsSelection}
          teamsIds={teamsIdsSelection}
          setTeamsIds={setTeamsIds}
          setTeamsIdsSelection={setTeamsIdsSelection}
          hasSelectedTeamOrLocation={hasSelectedTeamOrLocation}
          canManageAllCompany={canSeeInsightsForEverybody}
        />
        {showCallout && (
          <Callout
            type="info"
            title={calloutTitle}
            description={calloutDescription}
            onRemove={() => setShowQuickTip(false)}
            action={
              <Button type="secondary" onClick={() => setStopShowingQuickTip(true)}>
                {i18n.t('shift_management.actions.got_it_button')}
              </Button>
            }
          />
        )}
      </Box>
    </Modal>
  )
}

export default observer(InsightsRequestModal)
